import { zodResolver } from '@hookform/resolvers/zod'
import { isApiError } from '@pa/api'
import { AddUserRequest, type ClinicRole, type TempPasswordResult } from '@pa/api/clinic'
import { CloseButton, Modal, Segmented, useToast } from '@pa/ui'
import { useId, useState } from 'react'
import { Controller, useForm, type FieldPath } from 'react-hook-form'
import { z } from 'zod'
import { toastApiError } from '../../../errors'
import type { UsersMutation } from '../hooks/adapters'
import { addUserFieldMessage, EXISTING_ACCOUNT_COPY, ROLE_DESCRIPTION, ROLE_LABEL, USERS_ERRORS } from '../model/copy'

/** A basic email shape check for quick feedback; the server decides (API.md §6.19 `email_invalid`). */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * The generated `AddUserRequest` body schema (API.md §6.19), with the client-side checks the server repeats: a
 * name of 1–120 characters (DataModel `users.name`, D-63) and an email shape.
 */
const AddUserFormSchema = AddUserRequest.extend({
  name: z.string().trim().min(1, addUserFieldMessage('required')).max(120, addUserFieldMessage('name_length')),
  email: z
    .string()
    .trim()
    .min(1, addUserFieldMessage('required'))
    .refine((v) => v.length <= 254 && EMAIL_RE.test(v), addUserFieldMessage('email_invalid')),
})
type FormInput = z.input<typeof AddUserFormSchema>
type FormOutput = z.output<typeof AddUserFormSchema>

const FIELD_BY_PATH: Record<string, FieldPath<FormInput>> = { name: 'name', email: 'email', role: 'role' }

const ROLES: ClinicRole[] = ['admin', 'user']

export interface AddUserDialogProps {
  /** C-13a: User; S-05: Admin (UserFlows S-05). */
  defaultRole: ClinicRole
  add: UsersMutation<AddUserRequest, TempPasswordResult>
  onClose: () => void
  /** A new account (or a re-enabled one, D-61): the caller shows C-13b with the temporary password. */
  onTemporaryPassword: (result: TempPasswordResult) => void
}

/**
 * C-13a Add user (UserFlows C-13a) and S-05 Add clinic user (UserFlows S-05: the same dialog, role default Admin).
 * react-hook-form with the generated body schema; server issues land on their fields; `already_member` and
 * `account_not_addable` are email field errors. An existing account (D-41) gets no C-13b: the dialog says "They
 * can sign in with their existing password" and closes with Done.
 */
export function AddUserDialog({ defaultRole, add, onClose, onTemporaryPassword }: AddUserDialogProps) {
  const id = useId()
  const toast = useToast()
  const [linked, setLinked] = useState<string | null>(null)
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormInput, unknown, FormOutput>({
    resolver: zodResolver(AddUserFormSchema),
    defaultValues: { name: '', email: '', role: defaultRole },
  })

  async function onSubmit(v: FormOutput) {
    try {
      const result = await add.mutateAsync({ name: v.name, email: v.email.toLowerCase(), role: v.role })
      if (result.temporaryPassword) {
        onTemporaryPassword(result)
        return
      }
      // D-41: the account already exists at another clinic. Nothing secret to drop, but keep the cache clean.
      add.reset()
      setLinked(result.user.name)
    } catch (e) {
      if (isApiError(e) && e.code === 'conflict') {
        const reason = e.details?.reason
        if (reason === 'already_member' || reason === 'account_not_addable') {
          setError('email', { message: USERS_ERRORS[reason] }, { shouldFocus: true })
          return
        }
      }
      if (isApiError(e) && e.code === 'invalid_request' && e.issues?.length) {
        let mapped = false
        for (const issue of e.issues) {
          const field = FIELD_BY_PATH[issue.path.join('.')]
          if (field) {
            setError(field, { message: addUserFieldMessage(issue.code) }, { shouldFocus: !mapped })
            mapped = true
          }
        }
        if (mapped) return
      }
      toastApiError(toast, e)
    }
  }

  const fieldProps = (name: 'name' | 'email') => ({
    id: `${id}-${name}`,
    'aria-invalid': errors[name] ? true : undefined,
    'aria-describedby': errors[name] ? `${id}-${name}-error` : undefined,
  })

  if (linked !== null) {
    return (
      <Modal onClose={onClose} labelledBy={`${id}-done-title`} describedBy={`${id}-done-body`}>
        <div className="modal-head">
          <div>
            <h2 id={`${id}-done-title`}>User added</h2>
          </div>
          <CloseButton onClick={onClose} />
        </div>
        <div className="modal-body">
          <p id={`${id}-done-body`}>
            {linked} was added. {EXISTING_ACCOUNT_COPY}.
          </p>
        </div>
        <div className="modal-foot">
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal onClose={onClose} labelledBy={`${id}-title`}>
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
        <div className="modal-head">
          <div>
            <h2 id={`${id}-title`}>Add user</h2>
          </div>
          <CloseButton onClick={onClose} />
        </div>
        <div className="modal-body form-grid">
          <div className="field">
            <label className="label" htmlFor={`${id}-name`}>
              Name
            </label>
            <input className="input" autoComplete="off" {...fieldProps('name')} {...register('name')} />
            {errors.name?.message && (
              <span className="err-text" id={`${id}-name-error`}>
                {errors.name.message}
              </span>
            )}
          </div>
          <div className="field">
            <label className="label" htmlFor={`${id}-email`}>
              Email
            </label>
            <input
              className="input"
              type="email"
              autoComplete="off"
              spellCheck={false}
              {...fieldProps('email')}
              {...register('email')}
            />
            {errors.email?.message && (
              <span className="err-text" id={`${id}-email-error`}>
                {errors.email.message}
              </span>
            )}
          </div>
          <div className="field">
            <span className="label">Role</span>
            <Controller
              control={control}
              name="role"
              render={({ field }) => (
                <>
                  <Segmented
                    label="Role"
                    value={field.value}
                    options={ROLES.map((r) => ({ value: r, label: ROLE_LABEL[r] }))}
                    onChange={field.onChange}
                  />
                  <span className="hint">{ROLE_DESCRIPTION[field.value]}</span>
                </>
              )}
            />
          </div>
        </div>
        <div className="modal-foot">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Adding…' : 'Add'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
